import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { FloatingChat } from '../components/FloatingChat';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './SearchResultsPage.module.css';
import ErrorBanner from '../../../components/ErrorBanner';
import JurisdictionSelector from '../components/JurisdictionSelector';
import CategoryTree from '../components/CategoryTree';
import RegulationCard from '../components/RegulationCard';
import { 
  searchRegulations, 
  getJurisdictions, 
  getPolicyAreas,
  JurisdictionItem,
  PolicyAreaItem 
} from '../../../api/searchApi';
import { useSearchHistory } from '../../../context/SearchHistoryContext';

// Type for jurisdiction selector (hierarchical structure)
interface JurisdictionNode {
  id: string;
  name: string;
  children?: JurisdictionNode[];
}

// Type for category tree (hierarchical structure)
interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

type SearchResult = {
  id: string;
  title: string;
  snippet: string;
  summary: string | null;
  source: string;
  date: string;
  jurisdiction: string;
  jurisdictionCode: string;
  category: string;
  status?: string | null;
  aiDomains?: string[];
  hasFullText?: boolean;
  url: string;
  snippets: string[];
  billType?: string | null;
  billNumber?: string | null;
  why?: string | null;   // SRS #168 relevance explanation
  // Backend relevance: query terms matched (dominant) + AI-relevance tiebreak.
  score: number;
};

// Granular AI-domain labels (mirrors AI_DOMAIN_LABELS in backend ai_taxonomy.py)
// for the AI-taxonomy filter tabs + result badges (SOW requirement).
const AI_DOMAIN_LABELS: Record<string, string> = {
  foundation_models: 'Foundation Models / GPAI',
  high_risk: 'High-Risk AI Systems',
  prohibited: 'Prohibited / Banned Uses',
  biometric_surveillance: 'Biometric & Surveillance',
  transparency: 'Transparency & Disclosure',
  data_governance: 'Data Governance & Privacy',
  accountability: 'Governance & Accountability',
  sector: 'Sector-Specific AI',
  general_ai: 'General AI',
};

// Lifecycle status buckets for the Draft / Enforced / Revoked filter tabs.
// Mirrors STATUS_BUCKETS in backend/app/api/v1/search.py — classify a source's
// free-text status into one canonical bucket by keyword.
const STATUS_BUCKET_KEYWORDS: Record<string, string[]> = {
  enforced: ['enacted', 'in force', 'in effect', 'effective', 'adopted', 'promulgated', 'signed into law', 'came into force', 'final rule'],
  draft: ['introduced', 'proposed', 'pending', 'draft', 'consultation', 'prefiled', 'committee', 'referred', 'reported', 'reading'],
  revoked: ['withdrawn', 'vetoed', 'repealed', 'revoked', 'lapsed', 'died', 'defeated', 'dead', 'failed', 'expired', 'superseded', 'prorogued'],
};
const STATUS_TABS: { id: string; label: string }[] = [
  { id: 'enforced', label: 'Enforced' },
  { id: 'draft', label: 'Draft / Proposed' },
  { id: 'revoked', label: 'Revoked / Inactive' },
];
const statusBucketOf = (raw?: string | null): string | null => {
  const s = (raw || '').toLowerCase();
  if (!s) return null;
  for (const [bucket, kws] of Object.entries(STATUS_BUCKET_KEYWORDS)) {
    if (kws.some(kw => s.includes(kw))) return bucket;
  }
  return null;
};

const SearchResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';
  const baseSearchPath = location.pathname.endsWith('/results')
    ? location.pathname.replace(/\/results$/, '')
    : location.pathname;
  const isEmbeddedSearch =
    baseSearchPath.startsWith('/admin/dashboard/search') ||
    baseSearchPath.startsWith('/enterprise/dashboard/search') ||
    baseSearchPath.startsWith('/startup/dashboard/search') ||
    baseSearchPath.startsWith('/government/dashboard/search');

  const buildResultsPath = (value: string) => {
    const encodedQuery = encodeURIComponent(value);
    return isEmbeddedSearch
      ? `${baseSearchPath}?q=${encodedQuery}`
      : `/search/results?q=${encodedQuery}`;
  };

  const backToSearchPath = isEmbeddedSearch ? baseSearchPath : '/search';
  
  // State for filters
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);   // 'enforced' | 'draft' | 'revoked'
  const [selectedYear, setSelectedYear] = useState<string>('');             // '' = any year
  const [selectedAiDomains, setSelectedAiDomains] = useState<string[]>([]);  // AI-taxonomy domain keys
  const [searchText, setSearchText] = useState<string>('');
  const [sortBy, setSortBy] = useState('relevance');
  
  // State for search results
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalResults, setTotalResults] = useState<number | null>(null);
  const [corrections, setCorrections] = useState<Record<string, string> | null>(null);   // SRS #168
  const [exact, setExact] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  // The results grid is repeat(auto-fill, minmax(350px, 1fr)), so it is two,
  // three or four columns wide depending on the window. Eight filled none of
  // those evenly and left a visible gap in the last row — reported as "8 tiles
  // where it still has a space for one additional tile". Twelve divides by two,
  // three and four, so the last row is full at every width.
  const ITEMS_PER_PAGE = 12;
  const { addToSearchHistory } = useSearchHistory();

  // State for filter options (fetched from API)
  const [jurisdictionsData, setJurisdictionsData] = useState<JurisdictionNode[]>([]);
  const [categoriesData, setCategoriesData] = useState<CategoryNode[]>([]);
  const [baseJurisdictionIds, setBaseJurisdictionIds] = useState<Set<string>>(new Set());
  const [baseCategoryIds, setBaseCategoryIds] = useState<Set<string>>(new Set());
  const [jurisdictionCounts, setJurisdictionCounts] = useState<Record<string, number>>({});
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  // Fetch jurisdictions and policy areas on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch jurisdictions
        const jurisdictions = await getJurisdictions();
        // Group jurisdictions by type for hierarchical display
        const groupedJurisdictions: Record<string, JurisdictionItem[]> = {};
        jurisdictions.forEach(j => {
          const type = j.type || 'Other';
          if (!groupedJurisdictions[type]) {
            groupedJurisdictions[type] = [];
          }
          groupedJurisdictions[type].push(j);
        });
        // Convert to hierarchical structure
        const jurisdictionNodes: JurisdictionNode[] = Object.entries(groupedJurisdictions).map(
          ([type, items]) => ({
            id: type.toLowerCase().replace(/\s+/g, '-'),
            name: type,
            children: items.map(item => ({
              id: item.code,
              name: item.name,
            })),
          })
        );
        // If no jurisdictions from API, use default
        if (jurisdictionNodes.length === 0) {
          jurisdictionNodes.push({
            id: 'north-america',
            name: 'North America',
            children: [{ id: 'US', name: 'United States' }],
          });
        }
        setJurisdictionsData(jurisdictionNodes);

        // Fetch policy areas (categories)
        const policyAreas = await getPolicyAreas();
        // Convert to category tree structure - flat list since policy areas aren't hierarchical
        const categoryNodes: CategoryNode[] = policyAreas.map(pa => ({
          id: pa.name.toLowerCase().replace(/\s+/g, '-'),
          // keep plain name here; counts are shown via the badge UI
          name: pa.name,
        }));
        // If no categories from API, use defaults
        if (categoryNodes.length === 0) {
          categoryNodes.push(
            { id: 'ai-policy', name: 'AI Policy' },
            { id: 'data-governance', name: 'Data Governance' },
            { id: 'technology', name: 'Technology' }
          );
        }
        setCategoriesData(categoryNodes);
      } catch (err) {
        console.error('Error fetching filter options:', err);
        // Use defaults on error
        setJurisdictionsData([
          {
            id: 'north-america',
            name: 'North America',
            children: [{ id: 'US', name: 'United States' }],
          },
        ]);
        setCategoriesData([
          { id: 'ai-policy', name: 'AI Policy' },
          { id: 'data-governance', name: 'Data Governance' },
        ]);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch search results when component mounts or query changes
  useEffect(() => {
    const fetchResults = async () => {
      const searchParams = new URLSearchParams(location.search);
      const query = searchParams.get('q') || '';
      setSearchText(query);

      if (!query) return;
      
      // Add to search history
      addToSearchHistory(query);
      
      setIsLoading(true);
      setError(null);
      
      try {
        // First fetch base (unfiltered) results for this query to derive
        // which jurisdictions/categories have matches. This allows a
        // dynamic filter list (only show filters with matches) while still
        // permitting multiple selections among those available options.
        const baseResponse = await searchRegulations({
          query,
          size: 100,
        });

        const baseJurIdsSet = new Set(
          baseResponse.results
            .map((r: any) => r.jurisdiction_code)
            .filter((c: any) => !!c)
        );
        setBaseJurisdictionIds(baseJurIdsSet);

        // Use server-side jurisdiction aggregation counts when available;
        // fall back to counting from the result items (limited to size=50).
        const jurCounts: Record<string, number> = {};
        if ((baseResponse as any).jurisdiction_counts && Array.isArray((baseResponse as any).jurisdiction_counts)) {
          (baseResponse as any).jurisdiction_counts.forEach((jc: any) => {
            const key = jc.code || jc.name;
            if (key) jurCounts[key] = jc.count || 0;
          });
        } else {
          baseResponse.results.forEach((r: any) => {
            const code = r.jurisdiction_code || r.jurisdiction;
            if (code) jurCounts[code] = (jurCounts[code] || 0) + 1;
          });
        }
        setJurisdictionCounts(jurCounts);

        // Also rebuild baseJurisdictionIds from the aggregation so the sidebar
        // shows every jurisdiction that has any match, not just those in the
        // first 50 result items.
        if ((baseResponse as any).jurisdiction_counts && Array.isArray((baseResponse as any).jurisdiction_counts)) {
          const aggJurIds = new Set<string>(
            (baseResponse as any).jurisdiction_counts
              .map((jc: any) => jc.code || jc.name)
              .filter((c: any) => !!c)
          );
          setBaseJurisdictionIds(aggJurIds);
        }

        const baseCatIdsSet = new Set(
          baseResponse.results
            .map((r: any) => (r.policy_area || '').toLowerCase().replace(/\s+/g, '-'))
            .filter((c: any) => !!c)
        );
        setBaseCategoryIds(baseCatIdsSet);

        // compute counts per category from baseResponse
        const catCounts: Record<string, number> = {};
        baseResponse.results.forEach((r: any) => {
          const id = (r.policy_area || '').toLowerCase().replace(/\s+/g, '-');
          if (id) catCounts[id] = (catCounts[id] || 0) + 1;
        });
        setCategoryCounts(catCounts);

        // If the base response includes policy area counts, use those to
        // populate the category list so the filter UI reflects all
        // categories that have matches for the unfiltered query.
        if ((baseResponse as any).policy_area_counts && Array.isArray((baseResponse as any).policy_area_counts)) {
          const scoped: PolicyAreaItem[] = (baseResponse as any).policy_area_counts;
          const categoryNodes: CategoryNode[] = scoped.map(pa => ({
            id: (pa.name || 'general').toLowerCase().replace(/\s+/g, '-'),
            // keep plain name; counts displayed in badge
            name: pa.name || 'General',
          }));
          if (categoryNodes.length > 0) setCategoriesData(categoryNodes);
        }

        // Fetch the full result set for this query, UNFILTERED. Jurisdiction
        // and category filtering is applied client-side (see filteredResults
        // below), so toggling a filter never triggers a server refetch — which
        // is what blanked results ("data disappears after search") when a
        // filtered refetch returned empty. The fetch is driven purely by the
        // URL query (?q=), so the results URL reloads/shares correctly
        // regardless of cache state.
        const response = await searchRegulations({
          query,
          size: 100,
          ...(exact ? { exact: true } : {}),
        });
        setCorrections((response as any).corrections ?? null);

        const getSourceLabel = (
          rawSource: string | null | undefined,
          jurisdiction: string | null | undefined
        ): string => {
          switch (rawSource) {
            case 'congress_bills':
              return 'US Congress';
            case 'state_bills':
              return 'US State Legislature';
            case 'federal_register':
              return 'US Federal Register';
            case 'middle_east':
              return 'Middle East Pipeline';
            case 'china':
              return 'China Pipeline';
            case 'brazil':
              return 'Brazil Pipeline';
            case 'gazette':
              return 'India Gazette';
            case 'meity_english':
              return 'MeitY (India)';
            case 'press_release':
              return 'Press Release';
            case 'national_strategy_ai':
              return 'National AI Strategy';
            case 'information_technology':
              return 'Information Technology';
            case 'ai_for_inclusive':
              return 'AI for Inclusive';
            case 'pdpc_singapore':
              return 'PDPC Singapore';
            case 'legislation_gov_uk':
              return 'UK Legislation';
            case 'eu_pipeline':
              return 'European Pipeline';
            case 'regulations':
              return 'General Regulations';
            default:
              // Fallback: prettify unknown source
              if (rawSource && typeof rawSource === 'string') {
                return rawSource.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              }
              return 'Other Source';
          }
        };

        // NOTE: we intentionally do not overwrite `categoriesData` here with
        // counts from the filtered response. Category list should reflect
        // the set of categories that had matches for the original query
        // (the base response) so users can select multiple among them.

        // Transform API results to match component structure
        const transformedResults: SearchResult[] = response.results.map((result) => {
          const rawTitle = result.title || 'Untitled';
          const billLabel = (result as any).bill_type_label || `${result.bill_type || ''} ${result.bill_number || ''}`.trim();

          const snippet =
            result.snippets.length > 0
              ? result.snippets[0]
              : result.summary || rawTitle || 'No description available';

          return {
            id: result.id,
            title: rawTitle,
            summary: result.summary,
            snippet,
            snippets: result.snippets,
            source: getSourceLabel(result.source, result.jurisdiction),
            // Use actual action_date from API, not today's date
            date: result.action_date || '',
            // Use actual jurisdiction from API
            jurisdiction: result.jurisdiction || 'Unknown',
            jurisdictionCode: result.jurisdiction_code || '',
            // Use actual policy_area from API as category
            category: result.policy_area || 'General',
            status: result.status || null,
            aiDomains: result.ai_domains || [],
            // Absent on an older response: assume text is held rather than
            // labelling every result "reference only".
            hasFullText: result.has_full_text !== false,
            score: result.score ?? 0,
            url: `/regulations/${result.id}`,
            billType: billLabel || null,
            billNumber: result.bill_number,
            why: result.why ?? null,
          };
        });

        setResults(transformedResults);
        // Use server-provided true total (from COUNT query) when available;
        // fall back to the length of returned results.
        setTotalResults((response as any).total_results ?? transformedResults.length);
        console.log('Search results:', transformedResults);
      } catch (err) {
        console.error('Search error:', err);
        const fallback = 'We couldn’t fetch results right now. Please try again later.';
        let detail = fallback;
        if (err instanceof Error && err.message) {
          detail = err.message.includes('OpenSearch')
            ? 'Search service is not configured. Please contact an administrator.'
            : err.message;
        }
        setError(detail);
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
    // Refetch ONLY when the URL query (?q=) changes — not on every filter
    // toggle (filtering is client-side now) and not on addToSearchHistory
    // identity churn. This keeps the result set stable so it can't blank out,
    // and makes the page fully driven by the shareable/reloadable URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, exact]);   // exact: re-run "as typed" after a spelling correction (SRS #168)

  // Handle jurisdiction selection
  const handleJurisdictionChange = (selected: string[]) => {
    setSelectedJurisdictions(selected);
  };

  // Handle category selection
  const handleCategoryChange = (selected: string[]) => {
    setSelectedCategories(selected);
  };


  // Filter results based on selected jurisdictions and categories
  // Note: We don't filter by searchText here because the backend already returns
  // relevant results ranked by OpenSearch. Local filtering would incorrectly
  // exclude semantically relevant results that don't contain the exact search terms.
  const filteredResults = results.filter(result => {
    // Match by jurisdiction code, name, or the country-prefix of the code
    // (e.g. a "US-CA" row matches the selected "US"). Mirrors the backend
    // filter so this client-side pass never drops a row the server matched.
    const codePrefix = (result.jurisdictionCode || '').split('-')[0];
    const matchesJurisdiction = selectedJurisdictions.length === 0 ||
      selectedJurisdictions.includes(result.jurisdictionCode) ||
      selectedJurisdictions.includes(result.jurisdiction) ||
      (codePrefix !== '' && selectedJurisdictions.includes(codePrefix));

    // Match by category (policy area) - need to match the id format
    const categoryId = result.category.toLowerCase().replace(/\s+/g, '-');
    const matchesCategory = selectedCategories.length === 0 ||
      selectedCategories.includes(categoryId) ||
      selectedCategories.includes(result.category);

    // Match by lifecycle status bucket (Enforced / Draft / Revoked).
    const bucket = statusBucketOf((result as any).status);
    const matchesStatus = selectedStatuses.length === 0 ||
      (bucket !== null && selectedStatuses.includes(bucket));

    // Match by year of the action/effective date (date is an ISO string).
    const yr = String((result as any).date || '').slice(0, 4);
    const matchesYear = !selectedYear || yr === selectedYear;

    // Match by AI domain (SOW taxonomy) — result carries 0+ domain keys.
    const domains: string[] = (result as any).aiDomains || [];
    const matchesAiDomain = selectedAiDomains.length === 0 ||
      domains.some(d => selectedAiDomains.includes(d));

    return matchesJurisdiction && matchesCategory && matchesStatus && matchesYear && matchesAiDomain;
  });

  // Reset to page 1 when filters/sort change
  useEffect(() => { setCurrentPage(1); }, [selectedJurisdictions, selectedCategories, selectedStatuses, selectedYear, selectedAiDomains, sortBy, location.search]);

  // Years present in the current result set (for the Year filter dropdown).
  const availableYears = Array.from(
    new Set(results.map(r => String((r as any).date || '').slice(0, 4)).filter(y => /^\d{4}$/.test(y)))
  ).sort((a, b) => b.localeCompare(a));

  const toggleStatus = (id: string) =>
    setSelectedStatuses(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

  const toggleAiDomain = (id: string) =>
    setSelectedAiDomains(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

  // AI domains present in the current result set (for the AI-taxonomy filter).
  const availableAiDomains = Array.from(
    new Set(results.flatMap(r => ((r as any).aiDomains || []) as string[]))
  ).sort((a, b) => (AI_DOMAIN_LABELS[a] || a).localeCompare(AI_DOMAIN_LABELS[b] || b));

  // Sort results
  const sortedResults = [...filteredResults].sort((a, b) => {
    switch (sortBy) {
      case 'relevance':
        // Latest first, then backend relevance score (terms matched +
        // AI-relevance) as the tie-breaker — so the newest matching regulations
        // lead while still ranking by relevance within the same date.
        return (new Date(b.date).getTime() - new Date(a.date).getTime()) ||
          (b.score - a.score);
      case 'date-desc':
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      case 'date-asc':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });

  const filtersActive = selectedJurisdictions.length > 0 || selectedCategories.length > 0
    || selectedStatuses.length > 0 || selectedAiDomains.length > 0 || selectedYear !== '';

  const totalPages = Math.ceil(sortedResults.length / ITEMS_PER_PAGE);
  const pagedResults = sortedResults.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // Build dynamic lists based on the base (unfiltered) results so the
  // sidebar shows only filters that have matches for this query, while
  // still allowing multiple selection among those available options.
  const dynamicJurisdictionsData: JurisdictionNode[] = jurisdictionsData
    .map((group) => ({
      ...group,
      children: group.children?.filter((child) =>
        baseJurisdictionIds.has(child.id) || selectedJurisdictions.includes(child.id)
      ),
    }))
    .filter((group) => group.children && group.children.length > 0);

  const dynamicCategoriesData: CategoryNode[] = categoriesData.filter((cat) =>
    baseCategoryIds.has(cat.id) || selectedCategories.includes(cat.id)
  );

  // Handle result selection
  const handleResultSelect = (id: string) => {
    setSelectedResult(id === selectedResult ? null : id);
  };

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Handle search submission
  const handleMainSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchText.trim();
    if (query) {
      addToSearchHistory(query);
      navigate(buildResultsPath(query));
    }
  };

  return (
    <div className={`${styles.container} ${isEmbeddedSearch ? styles.containerEmbedded : ''}`}>
      <FloatingChat 
        isOpen={isChatOpen}
        onToggle={() => setIsChatOpen(!isChatOpen)}
        context="search"
      />
      <div className={`${styles.mainSearchContainer} ${isEmbeddedSearch ? styles.mainSearchContainerEmbedded : ''}`}>
        <div className={styles.mainSearchForm}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(backToSearchPath)}
            title="Back to Search"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>Search</span>
          </button>
          <form onSubmit={handleMainSearch} className={styles.mainSearchBox}>
            <input
              type="text"
              placeholder="Search regulations..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className={styles.mainSearchInput}
            />
            <button type="submit" className={styles.mainSearchButton}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
          
          <div className={styles.searchControls}>
            <div className={styles.sortOptions}>
              <label htmlFor="sort-by" className={styles.sortLabel}>Sort by:</label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.sortSelect}
              >
                <option value="relevance">Relevance</option>
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="title-asc">Title (A-Z)</option>
                <option value="title-desc">Title (Z-A)</option>
              </select>
            </div>
            
            <div className={styles.themeToggleContainer}>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.layout}>
        {/* Sidebar with filters */}
        {filtersOpen && (
          <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <span className={styles.sidebarTitle}>Filters</span>
              <button className={styles.closeFiltersBtn} onClick={() => setFiltersOpen(false)} title="Close filters">✕</button>
            </div>
            <div className={styles.filterSection}>
              <h3 className={styles.filterSectionTitle}>Jurisdictions</h3>
              <JurisdictionSelector
                jurisdictions={dynamicJurisdictionsData}
                selected={selectedJurisdictions}
                onChange={handleJurisdictionChange}
                counts={jurisdictionCounts}
              />
            </div>

            <div className={styles.filterSection}>
              <h3 className={styles.filterSectionTitle}>Categories</h3>
              {dynamicCategoriesData.length > 0 ? (
                <CategoryTree
                  categories={dynamicCategoriesData}
                  selected={selectedCategories}
                  onChange={handleCategoryChange}
                  counts={categoryCounts}
                />
              ) : (
                <p className={styles.filterEmptyNote}>
                  No policy-area categories apply to these results. Category tags
                  are currently available for US federal bills; other
                  jurisdictions aren&apos;t categorised yet.
                </p>
              )}
            </div>

            <div className={styles.filterSection}>
              <h3 className={styles.filterSectionTitle}>Status</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {STATUS_TABS.map(tab => {
                  const active = selectedStatuses.includes(tab.id);
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => toggleStatus(tab.id)}
                      aria-pressed={active}
                      style={{
                        padding: '0.35rem 0.7rem',
                        borderRadius: 999,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        border: `1px solid ${active ? 'var(--ds-accent, var(--ds-brand-blue))' : 'var(--ds-border, #d1d5db)'}`,
                        background: active ? 'var(--ds-accent, var(--ds-brand-blue))' : 'transparent',
                        color: active ? 'var(--ds-text-on-dark)' : 'var(--ds-text, #374151)',
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <p className={styles.filterEmptyNote} style={{ marginTop: '0.4rem' }}>
                Draft, Enforced &amp; Revoked are derived from each source&apos;s status.
              </p>
            </div>

            {availableAiDomains.length > 0 && (
              <div className={styles.filterSection}>
                <h3 className={styles.filterSectionTitle}>AI Domain</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {availableAiDomains.map(dom => {
                    const active = selectedAiDomains.includes(dom);
                    return (
                      <button
                        key={dom}
                        type="button"
                        onClick={() => toggleAiDomain(dom)}
                        aria-pressed={active}
                        style={{
                          padding: '0.35rem 0.7rem',
                          borderRadius: 999,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          border: `1px solid ${active ? 'var(--ds-accent, var(--ds-brand-blue))' : 'var(--ds-border, #d1d5db)'}`,
                          background: active ? 'var(--ds-accent, var(--ds-brand-blue))' : 'transparent',
                          color: active ? 'var(--ds-text-on-dark)' : 'var(--ds-text, #374151)',
                        }}
                      >
                        {AI_DOMAIN_LABELS[dom] || dom}
                      </button>
                    );
                  })}
                </div>
                <p className={styles.filterEmptyNote} style={{ marginTop: '0.4rem' }}>
                  Auto-classified into AI domains (Foundation Models, High-Risk, etc.).
                </p>
              </div>
            )}

            <div className={styles.filterSection}>
              <h3 className={styles.filterSectionTitle}>Year</h3>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.45rem 0.6rem',
                  borderRadius: 8,
                  border: '1px solid var(--ds-border, #d1d5db)',
                  background: 'var(--ds-surface, #fff)',
                  color: 'var(--ds-text, #374151)',
                  fontSize: '0.85rem',
                }}
              >
                <option value="">Any year</option>
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </aside>
        )}

        {/* Main content */}
        <main className={styles.mainContent}>

          {/* Search results */}
          <div className={styles.resultsContainer}>
            {error && (
              <div className={styles.resultsError}>
                <ErrorBanner
                  message="We couldn’t fetch results right now."
                  details={error}
                  onRetry={() => window.location.reload()}
                  align="center"
                />
              </div>
            )}

            {isLoading ? (
              <div className={styles.loading}>
                <div className={styles.spinner} data-testid="loading-spinner"></div>
                <p>Loading results...</p>
              </div>
            ) : sortedResults.length > 0 ? (
              <>
                <div className={styles.resultsHeader}>
                  <h2>Search results for "{query}"</h2>
                  {/* With a filter applied the list below is the filtered set,
                      so the count has to be too. It always showed the server's
                      unfiltered total — reported as "the total result found
                      count is not getting updated" when filters are applied.
                      Unfiltered it stays the server total, which can exceed the
                      page of results fetched. */}
                  <p data-testid="result-count">
                    {filtersActive
                      ? `${sortedResults.length} of ${totalResults !== null ? totalResults : sortedResults.length} results`
                      : `${totalResults !== null ? totalResults : sortedResults.length} results found`}
                  </p>
                  {corrections && Object.keys(corrections).length > 0 && (
                    <p data-testid="corrections" style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ds-text-secondary)' }}>
                      Showing results for <b>{Object.entries(corrections).map(([from, to]) => to).join(', ')}</b>
                      {' '}(corrected from {Object.keys(corrections).map((w) => `"${w}"`).join(', ')}).{' '}
                      <button type="button" onClick={() => setExact(true)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ds-link)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}>
                        Search for the words as typed
                      </button>
                    </p>
                  )}
                  {!filtersOpen && (
                    <button className={styles.openFiltersBtn} onClick={() => setFiltersOpen(true)}>
                      <Menu size={16} aria-hidden /> Filters
                    </button>
                  )}
                </div>

                <div className={styles.resultsList}>
                  {pagedResults.map((result) => (
                    <RegulationCard
                      key={result.id}
                      id={result.id}
                      title={result.title}
                      snippet={result.snippet}
                      source={result.source}
                      date={result.date}
                      url={result.url}
                      billType={result.billType}
                      isSelected={selectedResult === result.id}
                      onSelect={handleResultSelect}
                      query={query}
                      aiDomainBadges={(result.aiDomains || []).map(d => AI_DOMAIN_LABELS[d] || d)}
                      why={result.why}
                      hasFullText={result.hasFullText}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className={styles.pagination}>
                    <button className={styles.pageBtn} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
                    {(totalPages <= 7
                      ? Array.from({ length: totalPages }, (_, i) => i + 1)
                      : currentPage <= 4
                        ? [1, 2, 3, 4, 5, null, totalPages]
                        : currentPage >= totalPages - 3
                          ? [1, null, totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages]
                          : [1, null, currentPage-1, currentPage, currentPage+1, null, totalPages]
                    ).map((page, idx) => page === null
                      ? <span key={`e${idx}`} className={styles.pageBtn} style={{ cursor: 'default', border: 'none', background: 'none' }}>…</span>
                      : <button key={page} className={`${styles.pageBtn} ${page === currentPage ? styles.pageBtnActive : ''}`} onClick={() => setCurrentPage(page)}>{page}</button>
                    )}
                    <button className={styles.pageBtn} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.noResults}>
                <h3>No results found</h3>
                <p>Try adjusting your search or filter criteria.</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SearchResultsPage;
